import { useEffect, useRef, useState } from "react";
import { Camera, RefreshCw, Check, X } from "lucide-react";

export default function CameraCapture({ onCapture, onClose, facingMode = "user", testid = "camera-capture" }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [photo, setPhoto] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    navigator.mediaDevices
      .getUserMedia({ video: { facingMode } })
      .then((stream) => {
        if (!active) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) videoRef.current.srcObject = stream;
      })
      .catch(() => setError("Não foi possível acessar a câmera. Verifique a permissão do navegador e tente enviar uma imagem."));
    return () => {
      active = false;
      streamRef.current?.getTracks().forEach((t) => t.stop());
    };
  }, [facingMode]);

  const capture = () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return;
    const scale = Math.min(1, 800 / Math.max(video.videoWidth, video.videoHeight));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(video.videoWidth * scale);
    canvas.height = Math.round(video.videoHeight * scale);
    canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);
    setPhoto(canvas.toDataURL("image/jpeg", 0.85));
  };

  const confirm = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    onCapture(photo);
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-testid={testid}>
      <div className="absolute inset-0 bg-black/70" onClick={onClose} />
      <div className="relative bg-[#0D0D10] border border-white/10 rounded-2xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-4">
          <p className="font-display font-bold flex items-center gap-2">
            <Camera size={18} className="text-gold" /> Foto pela Câmera
          </p>
          <button type="button" onClick={onClose} data-testid={`${testid}-close`} className="text-zinc-400 hover:text-gold transition-colors" aria-label="Fechar câmera">
            <X size={20} />
          </button>
        </div>

        {error ? (
          <p className="text-sm text-zinc-400 border border-dashed border-white/15 rounded-xl p-6 text-center" data-testid={`${testid}-error`}>{error}</p>
        ) : photo ? (
          <img src={photo} alt="Captura" className="w-full rounded-xl border border-white/10" data-testid={`${testid}-preview`} />
        ) : (
          <video ref={videoRef} autoPlay playsInline className="w-full rounded-xl border border-white/10 bg-black" data-testid={`${testid}-video`} />
        )}

        {!error && (
          <div className="flex gap-3 mt-4">
            {photo ? (
              <>
                <button
                  type="button"
                  onClick={confirm}
                  data-testid={`${testid}-confirm`}
                  className="flex-1 inline-flex items-center justify-center gap-2 font-display font-semibold bg-gold text-obsidian px-6 py-3 rounded-full hover:bg-gold-amber transition-colors"
                >
                  <Check size={16} /> Usar esta Foto
                </button>
                <button
                  type="button"
                  onClick={() => setPhoto(null)}
                  data-testid={`${testid}-retake`}
                  className="inline-flex items-center gap-2 font-mono text-xs tracking-widest border border-white/15 text-zinc-400 px-5 py-3 rounded-full hover:border-gold/50 hover:text-gold transition-colors"
                >
                  <RefreshCw size={13} /> TIRAR OUTRA
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={capture}
                data-testid={`${testid}-shoot`}
                className="flex-1 inline-flex items-center justify-center gap-2 font-display font-semibold bg-gold text-obsidian px-6 py-3 rounded-full hover:bg-gold-amber transition-colors"
              >
                <Camera size={16} /> Capturar
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
